import { useCallback, useMemo, useState } from 'react';
import { ReactComponent as InspectIcon } from '@elementary/common-ui/assets/24x24/inspect.svg';
import { ReactComponent as ColumnsIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as TablesIcon } from '@elementary/common-ui/assets/tables.svg';
import styled from 'styled-components';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import Table, {
  TableFilters,
  TableEmptyState,
  QuickFilter,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import Humanize from 'humanize-plus';
import { Test } from 'shared/types';
import useTestManagementTableData from 'testManagement/hooks/useTestManagementTableData';
import { TestManagementTabs } from 'testManagement/types';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { space } from '@elementary/common-ui/style/utils';
import useTestManagementTableColumns from '../hooks/useTestManagementTableColumns';
import ChangeNotificationsDialog from './ChangeNotificationsDialog';
import DeleteTestDialog from './DeleteTestDialog';
import TestManagementActionsMenu from './TestManagementActionsMenu';
import AlertDestinationOverrideDialog from './AlertDestinationsOverride/AlertDestinationOverrideDialog';

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

type Props = {
  urlParamsPrefix: string;
  shouldConnectCodeRepo: boolean;
  mode: TestManagementTabs;
};

const TestManagementTable = ({
  urlParamsPrefix,
  shouldConnectCodeRepo,
  mode,
}: Props) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [notificationDialogMode, setNotificationDialogMode] = useState<
    'mute' | 'unmute' | ''
  >('');
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState<Test[]>([]);
  const [deleteTestsDialogOpen, setDeleteTestsDialogOpen] = useState(false);
  const [overrideDestinationsDialogOpen, setOverrideDestinationsDialogOpen] =
    useState(false);

  const { openSidePanel, closeSidePanel, selectedId } = useSidePanelParams();

  const {
    data,
    isLoading: loading,
    filterCounters,
  } = useTestManagementTableData({
    includeAlertDestinations: mode === 'alerts',
  });

  useViewLoadTracking({
    name:
      'Test management table > ' +
      `${mode === 'alerts' ? 'destinations' : 'configuration'}`,
    loadDeps: [(data?.length && !loading) || null],
  });

  const selectedItemsCount = useMemo(() => {
    if (isSelectAll) {
      if (excludedItems.length) {
        return filteredData.length - excludedItems.length;
      }
      return filteredData.length;
    }
    return selectedItems.length;
  }, [filteredData, excludedItems, isSelectAll, selectedItems]);

  const resetSelection = () => {
    setSelectedItems([]);
    setIsSelectAll(false);
    setExcludedItems([]);
  };

  const selectedTests = useMemo(() => {
    if (isSelectAll) {
      if (excludedItems.length) {
        return filteredData.filter((item) => !excludedItems.includes(item.id));
      }
      return filteredData;
    }
    return data.filter((item) => selectedItems.includes(item.id));
  }, [data, filteredData, excludedItems, isSelectAll, selectedItems]);

  const selectedTestsIds = useMemo(
    () => selectedTests.map((test) => test.id),
    [selectedTests],
  );

  const selectedTestNames = useMemo(
    () => selectedTests.map((test) => test.name),
    [selectedTests],
  );

  const chatContext = useMemo(() => {
    if (selectedTestsIds.length === 0) return undefined;
    return {
      testIds: selectedTestsIds,
      displayLabels: selectedTestNames,
    };
  }, [selectedTestsIds, selectedTestNames]);

  useChatContext(chatContext);

  const deleteDisabledReason = useMemo(() => {
    const hasCloudTests = selectedTests.some((test) => test.is_cloud);
    const hasNonCloudTests = selectedTests.some((test) => !test.is_cloud);

    if (hasCloudTests && hasNonCloudTests) {
      return 'Cannot delete cloud and dbt tests simultaneously';
    }

    if (shouldConnectCodeRepo && hasNonCloudTests) {
      return 'Connect your code repository to delete tests directly from the UI.';
    }

    return;
  }, [selectedTests, shouldConnectCodeRepo]);

  const editAlertDestinationsInBulkDisabledReason = useMemo(() => {
    const hasCodeConfiguration = selectedTests.some(
      (test) => test.alert_destinations_type === 'CODE',
    );

    if (hasCodeConfiguration) {
      return 'Cannot edit alert destinations for tests with code configuration.';
    }

    return;
  }, [selectedTests]);

  const handleDeleteMultiple = () => {
    if (
      selectedTests.every((test) => test.is_cloud) ||
      !shouldConnectCodeRepo
    ) {
      setDeleteTestsDialogOpen(true);
    }
  };

  const onActionMenuChange = (action: string) => {
    switch (action) {
      case 'delete':
        handleDeleteMultiple();
        break;
      case 'alertDestinations':
        setOverrideDestinationsDialogOpen(true);
        break;
      case 'mute':
      case 'unmute':
        setNotificationDialogMode(action);
        break;
      default:
        break;
    }
  };

  const tableSearchFunc = useCallback((searchVal: string, row: Test) => {
    const searchColumns: (keyof Test)[] = [
      'database_name',
      'schema',
      'table',
      'name',
      'type',
      'severity',
      'owners',
      'model_owners',
      'tags',
      'model_tags',
    ];
    return searchColumns.some((col) =>
      row[col]?.toString().toLowerCase().includes(searchVal.toLowerCase()),
    );
  }, []);

  const columns = useTestManagementTableColumns(data, mode);

  const quickFilters: QuickFilter[] = [
    {
      name: `Table tests (${Humanize.intComma(filterCounters.table)})`,
      icon: <TablesIcon width={20} />,
      filters: [
        {
          property: 'column',
          operator: 'equals',
          value: [''],
        },
        {
          property: 'is_custom_sql',
          operator: 'notEquals',
          value: ['true'],
        },
      ],
    },
    {
      name: `Column tests (${Humanize.intComma(filterCounters.column)})`,
      icon: <ColumnsIcon width={20} />,
      filters: [
        {
          property: 'column',
          operator: 'notEquals',
          value: [''],
        },
        {
          property: 'is_custom_sql',
          operator: 'notEquals',
          value: ['true'],
        },
      ],
    },
    {
      name: `Custom query tests (${Humanize.intComma(
        filterCounters.singular,
      )})`,
      icon: <InspectIcon width={20} />,
      filters: [
        {
          property: 'is_custom_sql',
          operator: 'equals',
          value: ['true'],
        },
      ],
    },
  ];

  return (
    <FlexColumn
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        px: 3,
        gap: 3,
      }}
    >
      <TableHeader>
        <TableFilters
          data={data}
          columns={columns}
          urlParamsPrefix={urlParamsPrefix}
          search={search}
          onSearch={(val) => setSearch(val)}
          quickFilters={quickFilters}
          clearFiltersChipLabel={`All (${Humanize.intComma(
            filterCounters.all,
          )})`}
          disabled={loading}
        />
        <ToggleColumnsMenu
          columns={columns}
          localStorageKey={`${urlParamsPrefix}_table`}
        />
      </TableHeader>

      <Table
        key={mode}
        data={data}
        columns={columns}
        selectionColumn
        selectedItems={selectedItems}
        onSelectItem={(items) => setSelectedItems(items)}
        isSelectAll={isSelectAll}
        onSelectAllChange={setIsSelectAll}
        excludedItems={excludedItems}
        onExcludeItem={(items) => setExcludedItems(items)}
        defaultSortKey="updated_at"
        defaultSortDirection="desc"
        bottomScrollPadding={100}
        urlParamsPrefix={urlParamsPrefix}
        search={search}
        searchFunc={tableSearchFunc}
        onFilteredItemsChange={(items) => {
          setFilteredData(items);
        }}
        emptyStateRenderer={TableEmptyState}
        localStorageKey={`${urlParamsPrefix}_table`}
        isLoading={loading}
        onRowClick={(row) =>
          row.id === selectedId
            ? closeSidePanel()
            : openSidePanel(
                row.is_cloud ? 'cloud_test' : 'test',
                row.id,
                'test',
              )
        }
        focusedRows={selectedId ? [selectedId] : []}
      />

      <TestManagementActionsMenu
        mode={mode}
        selectedCount={selectedItemsCount}
        onChange={onActionMenuChange}
        onClose={resetSelection}
        deleteDisabledTooltip={deleteDisabledReason}
        editDestinationsDisabledTooltip={
          editAlertDestinationsInBulkDisabledReason
        }
      />

      <AlertDestinationOverrideDialog
        open={overrideDestinationsDialogOpen}
        onClose={() => setOverrideDestinationsDialogOpen(false)}
        selectedTests={selectedTestsIds}
      />

      <DeleteTestDialog
        open={deleteTestsDialogOpen}
        onClose={() => setDeleteTestsDialogOpen(false)}
        onDeleteSuccess={resetSelection}
        selectedTests={selectedTests}
      />

      <ChangeNotificationsDialog
        open={!!notificationDialogMode}
        onClose={() => setNotificationDialogMode('')}
        mode={notificationDialogMode}
        selectedTestsIds={selectedTestsIds}
      />
    </FlexColumn>
  );
};

export default TestManagementTable;
